import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import accountingApi from '../services/accountingApi';

function AccountHeadShow() {
    const { id } = useParams();
    const [accountHead, setAccountHead] = useState(null);
    const [accountGroups, setAccountGroups] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        loadAccountHead();
    }, [id]);

    const loadAccountHead = async () => {
        try {
            const response = await accountingApi.getAccountHead(id);
            if (response.success || response.id) {
                const head = response.data || response;
                setAccountHead(head);

                // Load account groups for this head
                const groupsResponse = await accountingApi.getAccountGroups({ account_head_id: id });
                if (groupsResponse.success) {
                    const groups = Array.isArray(groupsResponse.data) ? groupsResponse.data : (groupsResponse.data?.account_groups || []);
                    setAccountGroups(groups);
                }
            }
        } catch (error) {
            console.error('Error loading account head:', error);
        } finally {
            setLoading(false);
        }
    };

    if (loading) return <div className="p-8 text-center">Loading...</div>;
    if (!accountHead) return <div className="p-8 text-center">Account Head not found</div>;

    return (
        <div className="max-w-7xl mx-auto px-4 py-8">
            <div className="mb-6">
                <div className="flex items-center gap-3 mb-2">
                    <Link to="/accounting/account-heads" className="p-2 text-slate-400 hover:text-indigo-600 rounded-lg transition-colors">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                        </svg>
                    </Link>
                    <h1 className="text-2xl font-bold text-slate-800">{accountHead.name}</h1>
                </div>
                <p className="text-sm text-slate-600">{accountHead.account_type} Account Head</p>
            </div>

            {/* Account Head Details */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 mb-6">
                <h2 className="text-lg font-bold text-slate-800 mb-4">Details</h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div>
                        <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Name</p>
                        <p className="text-sm font-medium text-slate-900">{accountHead.name}</p>
                    </div>
                    <div>
                        <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Type</p>
                        <p className="text-sm font-medium text-slate-900">{accountHead.account_type}</p>
                    </div>
                    <div>
                        <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Code</p>
                        <p className="text-sm font-medium text-slate-900">{accountHead.code || '-'}</p>
                    </div>
                    {accountHead.description && (
                        <div className="md:col-span-3">
                            <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Description</p>
                            <p className="text-sm text-slate-700">{accountHead.description}</p>
                        </div>
                    )}
                </div>
            </div>

            {/* Account Groups */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
                <div className="px-6 py-4 border-b border-slate-200">
                    <h2 className="text-lg font-bold text-slate-800">Account Groups</h2>
                </div>
                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead className="bg-slate-50 border-b">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">Name</th>
                                <th className="px-6 py-3 text-right text-xs font-medium text-slate-500 uppercase">Opening Balance</th>
                                <th className="px-6 py-3 text-center text-xs font-medium text-slate-500 uppercase">Status</th>
                                <th className="px-6 py-3 text-center text-xs font-medium text-slate-500 uppercase">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y">
                            {accountGroups.length > 0 ? accountGroups.map(group => (
                                <tr key={group.id} className="hover:bg-slate-50">
                                    <td className="px-6 py-4 text-sm font-medium text-slate-900">{group.name}</td>
                                    <td className="px-6 py-4 text-sm text-right font-medium text-slate-900">
                                        ₹{(group.opening_balance || 0).toFixed(2)}
                                    </td>
                                    <td className="px-6 py-4 text-center">
                                        <span className={`text-xs px-2 py-1 rounded-full ${group.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                                            {group.is_active ? 'Active' : 'Inactive'}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 text-center text-sm">
                                        <Link
                                            to={`/accounting/reports/ledger-statement?account_group_id=${group.id}`}
                                            className="text-indigo-600 hover:text-indigo-900"
                                        >
                                            View Ledger
                                        </Link>
                                    </td>
                                </tr>
                            )) : (
                                <tr>
                                    <td colSpan="4" className="px-6 py-8 text-center text-slate-500">
                                        No account groups found for this account head
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}

export default AccountHeadShow;
